import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  itemStatusSchema, dataStatusSchema, recurrenceFrequencySchema, periodTypeSchema, type ComplianceItemOutput, type CreateComplianceItemInput, type UpdateComplianceItemInput,
  ITEM_STATUS_LABELS, DATA_STATUS_LABELS, JURISDICTION_CODES, type DataStatus, type ItemStatus, type RecurrenceFrequency, type PeriodType,
  type UserOutput,
  renewalBusinessDate, millisecondsUntilRenewalDay, documentRenewalDays, documentRenewalLabel, formatDocumentExpiry,
} from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { downloadFromApi } from '../lib/download.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { useComplianceServiceTypes } from '../hooks/useComplianceServiceTypes.js';
import { BulkActionBar, BulkActionButton, BulkResultToast, postBulk, StaffSelect, type BulkResult } from '../components/BulkActionBar.js';
import { SortableTh, TablePager, useTableControls } from '../components/TableControls.js';
import { FilterBar, EmptyFilterRow, type ActiveFilter } from '../components/FilterBar.js';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { ClientSearchSelect } from '../components/ClientSearchSelect.js';
import { TableSkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { ViewIcon, EditIcon, DeleteIcon } from '../components/ActionIcons.js';
import { ComplianceBoard } from '../components/ComplianceBoard.js';
import { ComplianceCalendar } from '../components/ComplianceCalendar.js';
import { ClipboardCheck, Table as TableIcon, LayoutGrid, Calendar, Paperclip } from 'lucide-react';
import { useEscape } from '../hooks/useEscape.js';
import type { ApiError } from '@1financial/api-client';
import { fmtDate } from '../lib/date.js';
import { DueDateOffsetField } from '../components/DueDateOffsetField.js';
import { DateInput } from '../components/DateInput.js';
import { useUserPreference } from '../lib/user-preferences.js';


function unwrap<T>(res: T | ApiError): T {
  if (res && typeof res === 'object' && 'ok' in res && (res as { ok?: boolean }).ok === false) {
    throw new Error((res as ApiError).message);
  }
  return res as T;
}

export function CompliancePage() {
  const { api, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [businessDay, setBusinessDay] = useState(() => renewalBusinessDate());
  useEffect(() => {
    let lastDay = renewalBusinessDate();
    let timer: ReturnType<typeof setTimeout>;
    const refresh = (force = false) => {
      const day = renewalBusinessDate();
      if (force || day !== lastDay) {
        lastDay = day;
        setBusinessDay(day);
        void queryClient.invalidateQueries({ predicate: (query) =>
          ['compliance-items', 'compliance-board-items', 'compliance-board-completed'].includes(String(query.queryKey[0])),
        });
      }
      clearTimeout(timer);
      timer = setTimeout(() => refresh(), millisecondsUntilRenewalDay() + 100);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh(true);
    };
    refresh();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [queryClient]);
  // Dashboard/deep-link filters take precedence over this user's saved preferences.
  const initParam = (k: string) => new URLSearchParams(window.location.search).get(k);
  const initEnumParam = (k: string, options: readonly string[]) => {
    const v = initParam(k) ?? '';
    return options.includes(v) ? v : '';
  };
  const [status, setStatus] = useUserPreference('compliance.status', initEnumParam('status', itemStatusSchema.options), {
    urlKeys: ['from', 'status'],
    validate: (value) => typeof value === 'string' && (value === '' || itemStatusSchema.options.includes(value as typeof itemStatusSchema.options[number])),
  });
  const [dataStatus, setDataStatus] = useUserPreference('compliance.dataStatus', initEnumParam('dataStatus', dataStatusSchema.options), {
    urlKeys: ['from', 'dataStatus'],
    validate: (value) => typeof value === 'string' && (value === '' || dataStatusSchema.options.includes(value as typeof dataStatusSchema.options[number])),
  });
  const [jurisdiction, setJurisdiction] = useUserPreference('compliance.jurisdiction', initEnumParam('jurisdiction', JURISDICTION_CODES), {
    urlKeys: ['from', 'jurisdiction'],
    validate: (value) => typeof value === 'string' && (value === '' || (JURISDICTION_CODES as readonly string[]).includes(value)),
  });
  const [serviceType, setServiceType] = useUserPreference('compliance.serviceType', initParam('serviceType') ?? '', { urlKeys: ['from', 'serviceType'] });
  // OPS 3.7 step 3: filter options derive from the Compliance Templates catalog.
  const serviceTypeFilter = useComplianceServiceTypes(jurisdiction);
  const [staffId, setStaffId] = useUserPreference('compliance.staffId', initParam('assigneeId') ?? initParam('assignee1Id') ?? '', { urlKeys: ['from', 'assigneeId', 'assignee1Id'] });
  const [staffEither] = useState(() => initParam('assigneeId') !== null);
  const [blockedOnly, setBlockedOnly] = useUserPreference('compliance.blockedOnly', initParam('blocked') === 'true', { urlKeys: ['from', 'blocked'] });
  const [openOnly, setOpenOnly] = useUserPreference('compliance.openOnly', initParam('open') === 'true', { urlKeys: ['from', 'open'] });
  const [dueBefore, setDueBefore] = useUserPreference('compliance.dueBefore', initParam('dueBefore') ?? '', { urlKeys: ['from', 'dueBefore'] });
  const [dueAfter, setDueAfter] = useUserPreference('compliance.dueAfter', initParam('dueAfter') ?? '', { urlKeys: ['from', 'dueAfter'] });
  const [dueDateBefore, setDueDateBefore] = useUserPreference('compliance.dueDateBefore', initParam('dueDateBefore') ?? '', { urlKeys: ['from', 'dueDateBefore'] });
  const [dueDateAfter, setDueDateAfter] = useUserPreference('compliance.dueDateAfter', initParam('dueDateAfter') ?? '', { urlKeys: ['from', 'dueDateAfter'] });
  const [noDueDate, setNoDueDate] = useUserPreference('compliance.noDueDate', initParam('noDueDate') === 'true', { urlKeys: ['from', 'noDueDate'] });
  const [assignedToMe] = useState(() => initParam('assignedToMe') === 'true');
  type ComplianceView = 'table' | 'board' | 'calendar';
  const [view, setView] = useUserPreference<ComplianceView>('compliance.view', 'table', {
    validate: (value) => value === 'table' || value === 'board' || value === 'calendar',
  });
  const [showCompletedCol, setShowCompletedCol] = useUserPreference('compliance.showCompletedColumn', false);
  const [pendingStatusIds, setPendingStatusIds] = useState<Set<string>>(new Set());
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<ComplianceItemOutput | null>(null);
  const [deleting, setDeleting] = useState<ComplianceItemOutput | null>(null);
  const params = new URLSearchParams(window.location.search);
  const clientId = params.get('clientId') || '';
  const [searchText, setSearchText] = useState('');

  const { canDo } = useCapabilities();
  const canBulkEdit = canDo('compliance.edit');
  const canBulkArchive = canDo('compliance.bulk_delete');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<'assign' | 'setStatus' | 'archive' | null>(null);
  const [bulkResult, setBulkResult] = useState<BulkResult | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const complianceClients = useQuery({
    queryKey: ['compliance-clients', { limit: 100 }],
    enabled: api !== null,
    queryFn: async () => {
      const data = await api!.get<{ items: { id: string; name: string; clientCode: string; jurisdiction: string | null }[]; total: number }>('/compliance/clients?limit=100');
      return unwrap(data).items;
    },
  });

  // Server-side free-text search: client name/company + item fields in one box.
  const search = searchText.trim();
  const clientFilterId = clientId;

  const { sortBy, sortOrder, cursor, pageIndex, toggleSort, goNext, goPrev } = useTableControls(initParam('sortBy') === 'dueDate' ? 'dueDate' : 'deadline', 'asc', [search, status, dataStatus, jurisdiction, serviceType, staffId, clientFilterId, blockedOnly, openOnly, dueBefore, dueAfter, dueDateBefore, dueDateAfter, noDueDate, assignedToMe, businessDay]);

  // Selection only makes sense for the rows currently on screen.
  useEffect(() => { setSelected(new Set()); }, [search, status, dataStatus, jurisdiction, serviceType, staffId, clientFilterId, blockedOnly, openOnly, dueBefore, dueAfter, dueDateBefore, dueDateAfter, noDueDate, assignedToMe, sortBy, sortOrder, cursor]);

  const list = useQuery({
    queryKey: ['compliance-items', { search, status, dataStatus, clientId: clientFilterId, jurisdiction, serviceType, staffId, blockedOnly, openOnly, dueBefore, dueAfter, dueDateBefore, dueDateAfter, noDueDate, assignedToMe, sortBy, sortOrder, cursor }],
    enabled: api !== null,
    queryFn: async () => {
      if (!api) throw new Error('not authenticated');
      const p = new URLSearchParams();
      if (status) p.set('status', status);
      if (dataStatus) p.set('dataStatus', dataStatus);
      if (clientFilterId) p.set('clientId', clientFilterId);
      if (search) p.set('search', search);
      if (jurisdiction) p.set('jurisdiction', jurisdiction);
      if (serviceType) p.set('serviceType', serviceType);
      if (staffId) p.set(staffEither ? 'assigneeId' : 'assignee1Id', staffId);
      if (blockedOnly) p.set('blocked', 'true');
      if (openOnly) p.set('open', 'true');
      if (dueBefore) p.set('dueBefore', dueBefore);
      if (dueAfter) p.set('dueAfter', dueAfter);
      if (dueDateBefore) p.set('dueDateBefore', dueDateBefore);
      if (dueDateAfter) p.set('dueDateAfter', dueDateAfter);
      if (noDueDate) p.set('noDueDate', 'true');
      if (assignedToMe) p.set('assignedToMe', 'true');
      p.set('limit', '50');
      p.set('sortBy', sortBy);
      p.set('sortOrder', sortOrder);
      if (cursor) p.set('cursor', cursor);
      const data = await api!.get<{ items: ComplianceItemOutput[]; total: number; nextCursor?: string | null }>(`/compliance/items?${p.toString()}`);
      return unwrap(data);
    },
  });

  // Board/calendar need every matching item, not one page — walk the cursor.
  // Same filters as the table; Completed stays server-hidden unless the caller
  // asks for it (the board's Completed column expands with status=Completed).
  const boardFilterKey = { search, status, dataStatus, clientId: clientFilterId, jurisdiction, serviceType, staffId, blockedOnly, openOnly, dueBefore, dueAfter, dueDateBefore, dueDateAfter, noDueDate, assignedToMe };
  async function fetchAllMatching(extraStatus?: string): Promise<ComplianceItemOutput[]> {
    const all: ComplianceItemOutput[] = [];
    let cursor: string | null = null;
    for (let page = 0; page < 10; page++) {
      const p = new URLSearchParams();
      const effStatus = extraStatus ?? status;
      if (effStatus) p.set('status', effStatus);
      if (dataStatus) p.set('dataStatus', dataStatus);
      if (clientFilterId) p.set('clientId', clientFilterId);
      if (search) p.set('search', search);
      if (jurisdiction) p.set('jurisdiction', jurisdiction);
      if (serviceType) p.set('serviceType', serviceType);
      if (staffId) p.set(staffEither ? 'assigneeId' : 'assignee1Id', staffId);
      if (!extraStatus && blockedOnly) p.set('blocked', 'true');
      if (!extraStatus && openOnly) p.set('open', 'true');
      if (dueBefore) p.set('dueBefore', dueBefore);
      if (dueAfter) p.set('dueAfter', dueAfter);
      if (dueDateBefore) p.set('dueDateBefore', dueDateBefore);
      if (dueDateAfter) p.set('dueDateAfter', dueDateAfter);
      if (noDueDate) p.set('noDueDate', 'true');
      if (assignedToMe) p.set('assignedToMe', 'true');
      p.set('limit', '100');
      p.set('sortBy', 'dueDate');
      p.set('sortOrder', 'asc');
      if (cursor) p.set('cursor', cursor);
      const data = await api!.get<{ items: ComplianceItemOutput[]; total: number; nextCursor?: string | null }>(`/compliance/items?${p.toString()}`);
      const page1 = unwrap(data);
      all.push(...page1.items);
      if (!page1.nextCursor) break;
      cursor = page1.nextCursor;
    }
    return all;
  }

  const boardItems = useQuery({
    queryKey: ['compliance-board-items', boardFilterKey],
    enabled: api !== null && view !== 'table',
    queryFn: () => fetchAllMatching(),
  });

  const completedItems = useQuery({
    queryKey: ['compliance-board-completed', boardFilterKey],
    enabled: api !== null && view === 'board' && showCompletedCol,
    queryFn: () => fetchAllMatching('Completed'),
  });

  const changeStatus = useMutation({
    mutationFn: async ({ id, status: next }: { id: string; status: string }) => {
      const data = await api!.patch<{ data: ComplianceItemOutput }>(`/compliance/items/${id}`, { body: JSON.stringify({ status: next }) });
      return unwrap(data);
    },
    onMutate: ({ id }) => setPendingStatusIds((s) => new Set(s).add(id)),
    onSettled: (_d, _e, vars) => setPendingStatusIds((s) => {
      const next = new Set(s);
      next.delete(vars.id);
      return next;
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-board-items'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-board-completed'] });
      queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
    },
    onError: (e) => alert(e instanceof Error ? e.message : 'Status change failed'),
  });

  const clients = useQuery({
    queryKey: ['clients', { status: 'ACTIVE', limit: 100, for: 'compliance-dropdown' }],
    enabled: api !== null,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async () => {
      const data = await api!.get<{ items: { id: string; name: string; company: string | null }[] }>('/clients?status=ACTIVE&all=true&limit=100');
      return unwrap(data).items;
    },
  });

  const users = useQuery({
    queryKey: ['users', { limit: 100 }],
    enabled: api !== null,
    queryFn: async () => {
      const data = await api!.get<{ items: UserOutput[] }>('/users?limit=100');
      return unwrap(data).items;
    },
  });

  const activeFilters: ActiveFilter[] = [
    ...(search ? [{ key: 'search', label: `Search: ${search}` }] : []),
    ...(status ? [{ key: 'status', label: `Status: ${ITEM_STATUS_LABELS[status as ItemStatus] ?? status}` }] : []),
    ...(dataStatus ? [{ key: 'dataStatus', label: `Data: ${DATA_STATUS_LABELS[dataStatus as DataStatus] ?? dataStatus}` }] : []),
    ...(jurisdiction ? [{ key: 'jurisdiction', label: `Jurisdiction: ${jurisdiction}` }] : []),
    ...(serviceType ? [{ key: 'serviceType', label: `Service: ${serviceType}` }] : []),
    ...(staffId ? [{ key: 'staffId', label: `Staff: ${users.data?.find((u) => u.id === staffId)?.displayName ?? staffId.slice(0, 8)}` }] : []),
    ...(blockedOnly ? [{ key: 'blocked', label: 'Blocked & stalled' }] : []),
    ...(openOnly ? [{ key: 'open', label: 'Open items only' }] : []),
    ...(dueBefore ? [{ key: 'dueBefore', label: `End on/before ${fmtDate(dueBefore)}` }] : []),
    ...(dueAfter ? [{ key: 'dueAfter', label: `End on/after ${fmtDate(dueAfter)}` }] : []),
    ...(dueDateBefore ? [{ key: 'dueDateBefore', label: `Due on/before ${fmtDate(dueDateBefore)}` }] : []),
    ...(dueDateAfter ? [{ key: 'dueDateAfter', label: `Due on/after ${fmtDate(dueDateAfter)}` }] : []),
    ...(noDueDate ? [{ key: 'noDueDate', label: 'No due date' }] : []),
  ];

  function removeFilter(key: string) {
    if (key === 'search') setSearchText('');
    else if (key === 'status') setStatus('');
    else if (key === 'dataStatus') setDataStatus('');
    else if (key === 'jurisdiction') { setJurisdiction(''); setServiceType(''); }
    else if (key === 'serviceType') setServiceType('');
    else if (key === 'staffId') setStaffId('');
    else if (key === 'blocked') setBlockedOnly(false);
    else if (key === 'open') setOpenOnly(false);
    else if (key === 'dueBefore') setDueBefore('');
    else if (key === 'dueAfter') setDueAfter('');
    else if (key === 'dueDateBefore') setDueDateBefore('');
    else if (key === 'dueDateAfter') setDueDateAfter('');
    else if (key === 'noDueDate') setNoDueDate(false);
  }

  function clearAllFilters() {
    setSearchText('');
    setStatus('');
    setDataStatus('');
    setJurisdiction('');
    setServiceType('');
    setStaffId('');
    setBlockedOnly(false);
    setOpenOnly(false);
    setDueBefore('');
    setDueAfter('');
    setDueDateBefore('');
    setDueDateAfter('');
    setNoDueDate(false);
  }

  const create = useMutation({
    mutationFn: async (input: CreateComplianceItemInput) => {
      const data = await api!.post<{ data: ComplianceItemOutput }>('/compliance/items', { body: JSON.stringify(input) });
      return unwrap(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
      setShowNew(false);
    },
  });

  const update = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: UpdateComplianceItemInput }) => {
      const data = await api!.patch<{ data: ComplianceItemOutput }>(`/compliance/items/${id}`, { body: JSON.stringify(patch) });
      return unwrap(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await unwrap(await api!.delete<null>(`/compliance/items/${id}`));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
      setDeleting(null);
    },
  });

  const clientMap = new Map((complianceClients.data ?? []).map((c) => [c.id, c]));
  const canEditCompleted = canDo('compliance.edit_completed');

  function exportComplianceItems() {
    const p = new URLSearchParams();
    if (status) p.set('status', status);
    if (dataStatus) p.set('dataStatus', dataStatus);
    if (clientFilterId) p.set('clientId', clientFilterId);
    if (search) p.set('search', search);
    if (jurisdiction) p.set('jurisdiction', jurisdiction);
    if (serviceType) p.set('serviceType', serviceType);
    if (staffId) p.set(staffEither ? 'assigneeId' : 'assignee1Id', staffId);
    if (blockedOnly) p.set('blocked', 'true');
    if (openOnly) p.set('open', 'true');
    if (dueBefore) p.set('dueBefore', dueBefore);
    if (dueAfter) p.set('dueAfter', dueAfter);
      if (dueDateBefore) p.set('dueDateBefore', dueDateBefore);
      if (dueDateAfter) p.set('dueDateAfter', dueDateAfter);
      if (noDueDate) p.set('noDueDate', 'true');
      if (assignedToMe) p.set('assignedToMe', 'true');
    p.set('sortBy', sortBy);
    p.set('sortOrder', sortOrder);
    downloadFromApi(`/v1/compliance/items/export?${p.toString()}`, 'compliance-items.csv')
      .catch((e) => alert(e instanceof Error ? e.message : 'Export failed'));
  }

  async function runBulk(payload: { action: string; params?: Record<string, unknown> }) {
    setBulkBusy(true);
    try {
      const result = await postBulk('/v1/compliance/items/bulk', { ...payload, ids: [...selected] });
      setBulkResult(result);
      setBulkAction(null);
      setSelected(new Set());
      void queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
    } catch (err) {
      setBulkResult({
        updated: 0,
        failed: [...selected].map((id) => ({ id, error: err instanceof Error ? err.message : 'Bulk request failed' })),
      });
    } finally {
      setBulkBusy(false);
    }
  }

  const visibleIds = (list.data?.items ?? []).map((i) => i.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  function toggleAllVisible() {
    setSelected(allVisibleSelected ? new Set() : new Set(visibleIds));
  }

  function toggleSelected(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Compliance</h1>
          <p className="text-sm text-muted">{list.data?.total ?? '—'} items</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex rounded-lg border border-border overflow-hidden self-start">
            {([
              ['table', TableIcon, 'Table'],
              ['board', LayoutGrid, 'Board'],
              ['calendar', Calendar, 'Calendar'],
            ] as const).map(([v, Icon, label]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                aria-pressed={view === v}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${view === v ? 'bg-bg-hover text-white' : 'text-muted hover:text-white'}`}
              >
                <Icon size={14} />{label}
              </button>
            ))}
          </div>
          <button
            onClick={exportComplianceItems}
            className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg"
          >
            Export
          </button>
          <button
            onClick={() => setShowImport(true)}
            className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg"
          >
            Import
          </button>
          <button
            onClick={() => setShowNew(true)}
            className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg"
          >
            + New compliance item
          </button>
        </div>
      </header>

      <FilterBar
        search={searchText}
        onSearchChange={setSearchText}
        searchPlaceholder="Search clients, items, remarks…"
        activeFilters={activeFilters}
        onRemoveFilter={removeFilter}
        onClearAll={clearAllFilters}
      >
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">All item statuses</option>
          {itemStatusSchema.options.map((s) => (
            <option key={s} value={s}>{ITEM_STATUS_LABELS[s] ?? s}</option>
          ))}
        </select>
        <select
          value={dataStatus}
          onChange={(e) => setDataStatus(e.target.value)}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">All data statuses</option>
          {dataStatusSchema.options.map((s) => (
            <option key={s} value={s}>{DATA_STATUS_LABELS[s] ?? s}</option>
          ))}
        </select>
        <select
          value={jurisdiction}
          onChange={(e) => {
            setJurisdiction(e.target.value);
            setServiceType('');
          }}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">All jurisdictions</option>
          {JURISDICTION_CODES.map((j) => (
            <option key={j} value={j}>{j}</option>
          ))}
        </select>
        <select
          value={serviceType}
          onChange={(e) => setServiceType(e.target.value)}
          disabled={!jurisdiction}
          className="bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent disabled:opacity-50"
        >
          <option value="">
            {jurisdiction
              ? (serviceTypeFilter.data && serviceTypeFilter.serviceTypes.length === 0
                ? 'No service types — see Admin → Compliance Templates'
                : 'All service types')
              : 'Select jurisdiction first'}
          </option>
          {serviceTypeFilter.serviceTypes.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <SearchableSelect
          value={staffId}
          onChange={setStaffId}
          placeholder="All staff"
          options={(users.data ?? []).filter((u) => u.status !== 'DISABLED').map((u) => ({ value: u.id, label: u.displayName }))}
          className="w-full bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
          wrapperClassName="w-full sm:w-52"
        />
      </FilterBar>

      {view === 'table' && (
      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[768px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label="Select all visible"
                  className="w-4 h-4 accent-emerald-500 cursor-pointer align-middle"
                />
              </th>
              <SortableTh label="Client" sortKey="client" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="Service" sortKey="serviceType" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="Jurisdiction" sortKey="jurisdiction" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="Status" sortKey="status" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="Data" sortKey="dataStatus" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="End date" sortKey="deadline" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <SortableTh label="Due date" sortKey="dueDate" sortBy={sortBy} sortOrder={sortOrder} onSort={toggleSort} />
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <TableSkeletonRows colSpan={9} />
            )}
            {list.error && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</td></tr>
            )}
            {list.data?.items.length === 0 && (
              activeFilters.length > 0
                ? <EmptyFilterRow colSpan={9} onClearAll={clearAllFilters} />
                : <tr><td colSpan={9}><EmptyState icon={ClipboardCheck} title="No compliance items yet." /></td></tr>
            )}
            {list.data?.items.map((item) => {
              const client = item.client ?? clientMap.get(item.clientId);
              return (
                <tr
                  key={item.id}
                  className="border-b border-border/50 hover:bg-bg-hover/40"
                >
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={() => toggleSelected(item.id)}
                      aria-label={`Select ${item.serviceType}`}
                      className="w-4 h-4 accent-emerald-500 cursor-pointer align-middle"
                    />
                  </td>
                  <td className="px-4 py-3 text-white">
                    {client ? client.name : '—'}
                  </td>
                  <td className="px-4 py-3 text-muted">
                    <span className="inline-flex items-center gap-1.5">
                      {item.serviceType}
                      {item.documentRenewal && (
                        <span
                          title="Document renewal — auto-created from an expiring document; confirming the renewed copy closes it"
                          className="inline-flex items-center text-[10px] font-semibold border pill-neutral rounded px-1 py-0.5"
                        >
                          R
                        </span>
                      )}
                      {item.evidenceCount > 0 && (
                        <span
                          title={`${item.evidenceCount} evidence file${item.evidenceCount === 1 ? '' : 's'} filed`}
                          className="inline-flex items-center gap-0.5 text-xs text-muted border border-border rounded px-1 py-0.5"
                        >
                          <Paperclip size={11} />{item.evidenceCount}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted">{item.jurisdiction ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${itemStatusColour(item.status)}`}>
                      {ITEM_STATUS_LABELS[item.status as ItemStatus] ?? item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted">{item.dataStatus ? (DATA_STATUS_LABELS[item.dataStatus as DataStatus] ?? item.dataStatus) : '—'}</td>
                  <td className="px-4 py-3 text-muted">
                    {item.documentRenewal && item.documentRenewalExpiryDate && item.status !== 'Completed' ? (
                      <>
                        <span>{formatDocumentExpiry(item.documentRenewalExpiryDate)}</span>
                        <span className={`block text-xs ${documentRenewalDays(item.documentRenewalExpiryDate) < 0 ? 'text-rose-400' : 'text-amber-300'}`}>
                          {documentRenewalLabel(item.documentRenewalExpiryDate)}
                        </span>
                      </>
                    ) : fmtDate(item.deadline)}
                  </td>
                  <td className="px-4 py-3 text-muted">{fmtDate(item.dueDate)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <ViewIcon to={`/compliance/${item.id}`} label="View item" />
                      {canBulkEdit && (canEditCompleted || item.status !== 'Completed') && <EditIcon onClick={() => setEditing(item)} label="Edit item" />}
                      {canBulkEdit && (canEditCompleted || item.status !== 'Completed') && <DeleteIcon onClick={() => setDeleting(item)} label="Delete item" />}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <TablePager
          pageIndex={pageIndex}
          count={list.data?.items.length ?? 0}
          limit={50}
          total={list.data?.total}
          hasNext={Boolean(list.data?.nextCursor)}
          onPrev={goPrev}
          onNext={() => goNext(list.data?.nextCursor)}
        />
      </div>
      )}

      {view === 'board' && (
        <ComplianceBoard
          items={boardItems.data}
          isLoading={boardItems.isLoading}
          error={(boardItems.error as Error | null) ?? null}
          clientMap={clientMap}
          userName={(id) => (id ? users.data?.find((u) => u.id === id)?.displayName ?? null : null)}
          canEdit={canBulkEdit}
          canEditCompleted={canEditCompleted}
          onChangeStatus={(id, next) => changeStatus.mutate({ id, status: next })}
          pendingIds={pendingStatusIds}
          completedItems={completedItems.data}
          completedLoading={completedItems.isLoading || completedItems.isFetching}
          showCompleted={showCompletedCol}
          onToggleCompleted={() => setShowCompletedCol((v) => !v)}
        />
      )}

      {view === 'calendar' && (
        <ComplianceCalendar
          items={boardItems.data}
          isLoading={boardItems.isLoading}
          error={(boardItems.error as Error | null) ?? null}
          clientMap={clientMap}
        />
      )}

      {showNew && <ComplianceItemFormModal mode="create" onClose={() => setShowNew(false)} onSubmit={(input) => create.mutate(input as CreateComplianceItemInput)} error={create.error?.message ?? null} submitting={create.isPending} />}
      {showImport && <ImportComplianceItemsModal onClose={() => setShowImport(false)} onSuccess={() => { setShowImport(false); queryClient.invalidateQueries({ queryKey: ['compliance-items'] }); }} users={users.data ?? []} clients={clients.data ?? []} />}
      {editing && <ComplianceItemFormModal mode="edit" item={editing} onClose={() => setEditing(null)} onSubmit={(patch) => update.mutate({ id: editing.id, patch })} error={update.error?.message ?? null} submitting={update.isPending} />}
      {deleting && (
        <ConfirmModal
          title="Delete compliance item"
          message={`Delete "${deleting.serviceType}"? Soft-deleted — restorable from Admin → Trash.`}
          onClose={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting.id)}
          error={remove.error?.message ?? null}
          submitting={remove.isPending}
          confirmText="Delete"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}

      {view === 'table' && selected.size > 0 && (canBulkEdit || canBulkArchive) && (
        <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())}>
          {canBulkEdit && <BulkActionButton onClick={() => setBulkAction('assign')}>Assign</BulkActionButton>}
          {canBulkEdit && <BulkActionButton onClick={() => setBulkAction('setStatus')}>Set status</BulkActionButton>}
          {canBulkArchive && <BulkActionButton danger onClick={() => setBulkAction('archive')}>Delete</BulkActionButton>}
        </BulkActionBar>
      )}

      {bulkAction === 'assign' && (
        <BulkItemAssignModal
          count={selected.size}
          busy={bulkBusy}
          onClose={() => setBulkAction(null)}
          onSubmit={(params) => void runBulk({ action: 'assign', params })}
        />
      )}

      {bulkAction === 'setStatus' && (
        <BulkItemStatusModal
          count={selected.size}
          busy={bulkBusy}
          onClose={() => setBulkAction(null)}
          onSubmit={(nextStatus) => void runBulk({ action: 'setStatus', params: { status: nextStatus } })}
        />
      )}

      {bulkAction === 'archive' && (
        <ConfirmModal
          title="Delete compliance items"
          message={`Delete ${selected.size} selected compliance item(s)? They are soft-deleted — restorable from Admin → Trash.`}
          onClose={() => setBulkAction(null)}
          onConfirm={() => void runBulk({ action: 'archive' })}
          error={null}
          submitting={bulkBusy}
          confirmText="Delete"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}

      {bulkResult && <BulkResultToast result={bulkResult} onClose={() => setBulkResult(null)} />}
    </div>
  );
}

/** OPS 3.5 #9: ONE compliance-item form for create AND edit. The client is
 *  picked only at creation (items are not re-assignable); edit sends the same
 *  field set minus crmClientId (the server strips it on PATCH anyway).
 *  OPS 3.5 #11: the client picker is the server-search ClientSearchSelect —
 *  the old pre-fetched page silently hid every client past the 100-row cap.
 *  OPS 3.7 step 3: service-type options come from the Compliance Templates
 *  catalog via useComplianceServiceTypes (no hardcoded list); a legacy value
 *  no template offers stays selectable as "(current)". */
export function ComplianceItemFormModal({ mode, item, onClose, onSubmit, error, submitting }: {
  mode: 'create' | 'edit';
  /** Required in edit mode. */
  item?: ComplianceItemOutput;
  onClose: () => void;
  /** Edit payload is UpdateComplianceItemInput (crmClientId optional there);
   *  the create call site casts to CreateComplianceItemInput. */
  onSubmit: (input: UpdateComplianceItemInput) => void;
  error: string | null;
  submitting: boolean;
}) {
  const users = useQuery({
    queryKey: ['users', { limit: 100 }],
    queryFn: async () => {
      const res = await fetch('/v1/users?limit=100', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const j = await res.json();
      return j.data.items as UserOutput[];
    },
  });

  const [form, setForm] = useState<UpdateComplianceItemInput>(() => mode === 'edit' && item ? {
    serviceType: item.serviceType,
    jurisdiction: item.jurisdiction,
    status: item.status as ItemStatus,
    dataStatus: item.dataStatus as DataStatus | null,
    deadline: item.deadline ? item.deadline.slice(0, 10) : null,
    dueDateOffsetDays: item.dueDateOffsetDays,
    dueDate: item.dueDate ? item.dueDate.slice(0, 10) : null,
    isRecurring: item.isRecurring,
    recurrenceFrequency: item.recurrenceFrequency,
    assignee1Id: item.assignee1Id,
    assignee2Id: item.assignee2Id,
    managerId: item.managerId,
    estimatedHours: item.estimatedHours,
  } : {
    crmClientId: '',
    serviceType: '',
    jurisdiction: null,
    status: 'Pending',
    dataStatus: null,
    deadline: null,
    dueDateOffsetDays: null,
    isRecurring: false,
    recurrenceFrequency: null,
    assignee1Id: null,
    assignee2Id: null,
    managerId: null,
    estimatedHours: null,
  });
  function update<K extends keyof UpdateComplianceItemInput>(k: K, v: UpdateComplianceItemInput[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // OPS 3.7 step 3: service types derive from the Compliance Templates
  // catalog for the picked jurisdiction. A legacy/current value that no
  // template offers stays selectable (rendered as an extra option) so editing
  // an old item never blanks its service type.
  const { serviceTypes, isLoading: serviceTypesLoading } = useComplianceServiceTypes(form.jurisdiction);
  const currentService = form.serviceType ?? '';
  const showLegacyService = currentService !== '' && currentService !== 'Other' && !serviceTypes.includes(currentService);

  const canSubmit = Boolean(
    (mode === 'edit' || form.crmClientId) &&
    form.jurisdiction && form.serviceType &&
    (mode === 'edit' || form.deadline) && (form.dueDateOffsetDays != null || form.dueDate != null || mode === 'edit') && form.status &&
    form.dataStatus && form.estimatedHours != null &&
    form.managerId && form.assignee1Id
  );

  const staffOptions = (users.data ?? []).filter((u: UserOutput) => u.status !== "DISABLED")
    .map((u: UserOutput) => ({ value: u.id, label: `${u.displayName} (${u.email})` }));

  return (
    <ModalShell title={mode === 'create' ? 'New compliance item' : 'Edit compliance item'} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); if (!canSubmit) return; onSubmit({ ...form, deadline: form.deadline ? form.deadline + 'T00:00:00Z' : null, dueDate: form.dueDateOffsetDays != null || form.dueDate === undefined ? undefined : form.dueDate ? form.dueDate + 'T00:00:00Z' : null, dueDateOffsetDays: form.dueDateOffsetDays }); }} className="space-y-3">
        {mode === 'create' && (
          <div>
            <label className="block text-xs text-muted mb-1">Client *</label>
            <ClientSearchSelect
              value={form.crmClientId ?? ''}
              onChange={(v) => update('crmClientId', v)}
              placeholder="— select —"
              required
              status="ACTIVE"
            />
            <p className="text-xs text-muted mt-1">Active client this compliance item belongs to.</p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-muted mb-1">Jurisdiction *</label>
            <select
              value={form.jurisdiction ?? ''}
              onChange={(e) => {
                const j = e.target.value || null;
                // The catalog is per-jurisdiction: switching jurisdiction
                // resets the service type, except switching BACK to the
                // item's own jurisdiction restores its original value.
                const keep = mode === 'edit' && item && j === item.jurisdiction ? (item.serviceType ?? '') : '';
                setForm((f) => ({ ...f, jurisdiction: j, serviceType: keep }));
              }}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
              required
            >
              <option value="">— select —</option>
              {JURISDICTION_CODES.map((j) => <option key={j} value={j}>{j}</option>)}
            </select>
            <p className="text-xs text-muted mt-1">e.g. UK — determines the available service types.</p>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Service type *</label>
            <select
              value={form.serviceType ?? ''}
              onChange={(e) => update('serviceType', e.target.value)}
              disabled={!form.jurisdiction}
              className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent disabled:opacity-50"
              required
            >
              <option value="">— select —</option>
              {serviceTypes.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
              {showLegacyService && <option value={currentService}>{currentService} (current)</option>}
              <option value="Other">Other</option>
            </select>
            {form.jurisdiction && !serviceTypesLoading && serviceTypes.length === 0 ? (
              <p className="text-xs text-amber-300/90 mt-1">No service types for {form.jurisdiction} yet — add one under Admin → Compliance Templates.</p>
            ) : (
              <p className="text-xs text-muted mt-1">Pick a service for the chosen jurisdiction.</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-muted mb-1">End date {mode === 'create' && '*'}</label>
            <DateInput value={form.deadline ?? ''} onChange={(e) => { update('deadline', e.target.value || null); }} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent" required={mode === 'create'} />
            <p className="text-xs text-muted mt-1">Actual period end, not the filing deadline. Unknown dates stay blank.</p>
          </div>
          <div className="space-y-2">
            <label className="block text-xs text-muted">Due date (filing/service)
              <DateInput value={form.dueDate ?? ''} onChange={(e) => { setForm((current) => ({ ...current, dueDate: e.target.value || null, dueDateOffsetDays: null })); }} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent" />
            </label>
            <DueDateOffsetField value={form.dueDateOffsetDays ?? null} onChange={(v) => setForm((current) => ({ ...current, dueDateOffsetDays: v, dueDate: undefined }))} endDate={form.deadline} required={mode === 'create' && !form.dueDate} />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-muted mb-1">Status *</label>
            <select value={form.status ?? ''} onChange={(e) => update('status', e.target.value as ItemStatus)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent" required>
              <option value="">—</option>
              {itemStatusSchema.options.map((s) => <option key={s} value={s}>{ITEM_STATUS_LABELS[s] ?? s}</option>)}
            </select>
            <p className="text-xs text-muted mt-1">{mode === 'create' ? 'Initial workflow status — usually Pending.' : 'Current workflow status of the item.'}</p>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Data status *</label>
            <select value={form.dataStatus ?? ''} onChange={(e) => update('dataStatus', e.target.value ? (e.target.value as DataStatus) : null)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent" required>
              <option value="">—</option>
              {dataStatusSchema.options.map((s) => <option key={s} value={s}>{DATA_STATUS_LABELS[s] ?? s}</option>)}
            </select>
            <p className="text-xs text-muted mt-1">Whether the client's data has been received.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-muted mb-1">Recurrence</label>
            <select value={form.recurrenceFrequency ?? ''} onChange={(e) => update('recurrenceFrequency', e.target.value ? (e.target.value as RecurrenceFrequency) : null)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent">
              <option value="">—</option>
              {recurrenceFrequencySchema.options.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <p className="text-xs text-muted mt-1">How often it repeats — only if Recurring is ticked.</p>
          </div>
          <div className="flex items-end gap-2 pb-2">
            <input id="item-recurring" type="checkbox" checked={form.isRecurring ?? false} onChange={(e) => update('isRecurring', e.target.checked)} className="rounded border-border bg-bg-base text-accent focus:ring-accent" />
            <label htmlFor="item-recurring" className="text-sm text-white">Recurring</label>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-muted mb-1">Manager <span className="text-rose-400">*</span></label>
            <SearchableSelect
              value={form.managerId ?? ''}
              onChange={(v) => update('managerId', v || null)}
              placeholder="—"
              required
              options={staffOptions}
            />
            <p className="text-xs text-muted mt-1">Overseeing manager.</p>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Assignee 1 <span className="text-rose-400">*</span></label>
            <SearchableSelect
              value={form.assignee1Id ?? ''}
              onChange={(v) => { const nv = v || null; setForm((f) => ({ ...f, assignee1Id: nv, assignee2Id: nv ? f.assignee2Id ?? null : null })); }}
              placeholder="—"
              required
              options={staffOptions}
            />
            <p className="text-xs text-muted mt-1">Primary person doing the work.</p>
          </div>
          <div>
            <label className="block text-xs text-muted mb-1">Assignee 2</label>
            <SearchableSelect
              value={form.assignee2Id ?? ''}
              onChange={(v) => update('assignee2Id', v || null)}
              placeholder="— optional"
              disabled={!form.assignee1Id}
              options={staffOptions}
            />
            <p className="text-xs text-muted mt-1">{form.assignee1Id ? 'Second person on this item.' : 'Choose assignee 1 first.'}</p>
          </div>
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Estimated hours *</label>
          <input type="number" value={form.estimatedHours ?? ''} onChange={(e) => update('estimatedHours', e.target.value ? Number(e.target.value) : null)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent" required />
          <p className="text-xs text-muted mt-1">Expected effort in hours, e.g. 2.5.</p>
        </div>
        {error && <p className="text-xs text-rose-400">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button type="submit" disabled={submitting || !canSubmit} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">{submitting ? (mode === 'create' ? 'Creating…' : 'Saving…') : (mode === 'create' ? 'Create item' : 'Save changes')}</button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEscape(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-lg mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-white">{title}</h2>
          <button onClick={onClose} className="text-muted hover:text-white">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ConfirmModal({ title, message, onClose, onConfirm, error, submitting, confirmText, confirmClass }: {
  title: string;
  message: string;
  onClose: () => void;
  onConfirm: () => void;
  error: string | null;
  submitting: boolean;
  confirmText: string;
  confirmClass: string;
}) {
  useEscape(onClose);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-bg-panel border border-border rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto mx-4 shadow-2xl animate-pop p-5">
        <h2 className="text-base font-semibold text-white mb-2">{title}</h2>
        <p className="text-sm text-muted mb-4">{message}</p>
        {error && <p className="text-xs text-rose-400 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button onClick={onConfirm} disabled={submitting} className={`text-sm font-medium px-4 py-1.5 rounded-lg disabled:opacity-50 ${confirmClass}`}>{submitting ? 'Working…' : confirmText}</button>
        </div>
      </div>
    </div>
  );
}

function itemStatusColour(status: string) {
  const map: Record<string, string> = {
    Pending: 'pill-neutral',
    InProcess: 'pill-info',
    ReadyForReview: 'pill-warn',
    Reviewed: 'pill-indigo',
    SentBack: 'pill-danger',
    SentToClient: 'pill-teal',
    Completed: 'pill-success',
  };
  return map[status] ?? 'pill-neutral';
}


function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];
    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(cell);
        cell = '';
      } else if (ch === '\n') {
        row.push(cell);
        rows.push(row);
        row = [];
        cell = '';
      } else if (ch !== '\r') {
        cell += ch;
      }
    }
  }
  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

const COMPLIANCE_IMPORT_HEADERS = ['clientName', 'serviceType', 'jurisdiction', 'deadline', 'dueDateOffsetDays', 'status', 'dataStatus', 'isRecurring', 'recurrenceFrequency', 'periodType', 'periodValue', 'assignee1Name', 'assignee1Email', 'assignee2Name', 'assignee2Email', 'managerName', 'managerEmail', 'estimatedHours', 'estimatedFeeAmount', 'estimatedFeeCurrency', 'remarks'];

function downloadTemplate(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function normalizeComplianceHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, '').replace(/[_-]/g, '');
}

function mapComplianceHeader(raw: string): string | null {
  const norm = normalizeComplianceHeader(raw);
  const aliases: Record<string, string> = {
    clientname: 'clientName',
    client: 'clientName',
    company: 'clientName',
    servicetype: 'serviceType',
    service: 'serviceType',
    jurisdiction: 'jurisdiction',
    country: 'jurisdiction',
    deadline: 'deadline',
    enddate: 'deadline',
    duedateoffsetdays: 'dueDateOffsetDays',
    offsetdays: 'dueDateOffsetDays',
    duedateoffset: 'dueDateOffsetDays',
    status: 'status',
    datastatus: 'dataStatus',
    isrecurring: 'isRecurring',
    recurring: 'isRecurring',
    recurrencefrequency: 'recurrenceFrequency',
    frequency: 'recurrenceFrequency',
    cadence: 'recurrenceFrequency',
    periodtype: 'periodType',
    periodvalue: 'periodValue',
    assignee1name: 'assignee1Name',
    assignee1: 'assignee1Name',
    assignee1email: 'assignee1Email',
    assignee2name: 'assignee2Name',
    assignee2: 'assignee2Name',
    assignee2email: 'assignee2Email',
    managername: 'managerName',
    manager: 'managerName',
    manageremail: 'managerEmail',
    estimatedhours: 'estimatedHours',
    hours: 'estimatedHours',
    estimatedfeeamount: 'estimatedFeeAmount',
    feeamount: 'estimatedFeeAmount',
    fee: 'estimatedFeeAmount',
    estimatedfeecurrency: 'estimatedFeeCurrency',
    feecurrency: 'estimatedFeeCurrency',
    currency: 'estimatedFeeCurrency',
    remarks: 'remarks',
    notes: 'remarks',
  };
  return aliases[norm] ?? null;
}

function parseComplianceImportRows(text: string): Array<Record<string, string>> {
  const grid = parseCsv(text.trim());
  if (grid.length < 2) return [];
  const headers = (grid[0] ?? []).map((h) => mapComplianceHeader(h)).map((h, idx) => h ?? `__col${idx}`);
  return grid.slice(1).map((row) => {
    const out: Record<string, string> = {};
    headers.forEach((h, idx) => {
      if (!h.startsWith('__col')) out[h] = row[idx]?.trim() ?? '';
    });
    return out;
  }).filter((r) => Object.values(r).some((v) => v.length > 0));
}

function ImportComplianceItemsModal({ onClose, onSuccess, users, clients }: { onClose: () => void; onSuccess: () => void; users: UserOutput[]; clients: { id: string; name: string; company: string | null }[] }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<Array<Record<string, string>>>([]);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{ created: number; failed: number; errors: string[] } | null>(null);
  const [dragOver, setDragOver] = useState(false);

  function handleFile(f: File | null) {
    setFile(f);
    setRows([]);
    setResult(null);
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result ?? '');
      const parsed = parseComplianceImportRows(text);
      setRows(parsed);
    };
    reader.readAsText(f);
  }

  function buildPayload() {
    return rows.map((r) => ({
      clientName: r.clientName || null,
      serviceType: r.serviceType || null,
      jurisdiction: r.jurisdiction?.trim() || null,
      deadline: r.deadline?.trim() || null,
      dueDateOffsetDays: r.dueDateOffsetDays?.trim() || null,
      status: r.status?.trim() || null,
      dataStatus: r.dataStatus?.trim() || null,
      isRecurring: r.isRecurring?.trim() || null,
      recurrenceFrequency: r.recurrenceFrequency?.trim() || null,
      periodType: r.periodType?.trim() || null,
      periodValue: r.periodValue?.trim() || null,
      assignee1Name: r.assignee1Name?.trim() || null,
      assignee1Email: r.assignee1Email?.trim() || null,
      assignee2Name: r.assignee2Name?.trim() || null,
      assignee2Email: r.assignee2Email?.trim() || null,
      managerName: r.managerName?.trim() || null,
      managerEmail: r.managerEmail?.trim() || null,
      estimatedHours: r.estimatedHours?.trim() || null,
      estimatedFeeAmount: r.estimatedFeeAmount?.trim() || null,
      estimatedFeeCurrency: r.estimatedFeeCurrency?.trim() || null,
      remarks: r.remarks?.trim() || null,
    })).filter((r) => r.clientName && r.serviceType);
  }

  function previewErrors(): Array<{ row: number; message: string }> {
    const errs: Array<{ row: number; message: string }> = [];
    rows.forEach((r, idx) => {
      if (!r.clientName?.trim()) errs.push({ row: idx + 2, message: 'Client name is required' });
      if (!r.serviceType?.trim()) errs.push({ row: idx + 2, message: 'Service type is required' });
      if (r.deadline && !/^\d{4}-\d{2}-\d{2}$/.test(r.deadline.trim())) errs.push({ row: idx + 2, message: `Deadline must be YYYY-MM-DD: ${r.deadline}` });
      if (r.dueDateOffsetDays && !/^\d+$/.test(r.dueDateOffsetDays.trim())) errs.push({ row: idx + 2, message: `Due date offset must be a number: ${r.dueDateOffsetDays}` });
      if (r.estimatedHours && !/^\d+(\.\d+)?$/.test(r.estimatedHours.trim())) errs.push({ row: idx + 2, message: `Estimated hours must be a number: ${r.estimatedHours}` });
      if (r.estimatedFeeAmount && !/^\d+(\.\d+)?$/.test(r.estimatedFeeAmount.trim())) errs.push({ row: idx + 2, message: `Estimated fee amount must be a number: ${r.estimatedFeeAmount}` });
      if (r.estimatedFeeCurrency && !/^[A-Z]{3}$/.test(r.estimatedFeeCurrency.trim())) errs.push({ row: idx + 2, message: `Currency must be 3 uppercase letters: ${r.estimatedFeeCurrency}` });
    });
    return errs.slice(0, 20);
  }

  async function run(dry = false) {
    setRunning(true);
    setResult(null);
    const payload = buildPayload();
    if (payload.length === 0) {
      setResult({ created: 0, failed: 0, errors: ['No valid rows to import.'] });
      setRunning(false);
      return;
    }
    try {
      const res = await fetch('/v1/compliance/items/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify({ rows: payload, dryRun: dry }),
      });
      const body = await res.json().catch(() => ({ ok: false, error: { message: res.statusText } }));
      if (!res.ok || !body.ok) {
        setResult({ created: 0, failed: payload.length, errors: [body.error?.message ?? 'Import failed'] });
      } else {
        const data = body.data as { created: number; failed: number; errors: Array<{ row: number; clientName?: string; serviceType?: string; field?: string; message: string }> };
        setResult({
          created: data.created,
          failed: data.failed,
          errors: data.errors.map((e) => `Row ${e.row}${e.field ? ` (${e.field})` : ''}: ${e.message}`),
        });
        queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
        if (!dry && data.created > 0) onSuccess();
      }
    } catch (e) {
      setResult({ created: 0, failed: payload.length, errors: [e instanceof Error ? e.message : String(e)] });
    }
    setRunning(false);
  }

  const template = [
    COMPLIANCE_IMPORT_HEADERS.join(','),
    'Acme Corp,UAE VAT Return,AE,2026-09-30,7,Pending,NotReceived,false,MONTHLY,MONTH,2026-09,,,,,Sohail Ahmad,sohail@1financial.net,5,500,AED,Monthly VAT return'
  ].join('\n');
  const errs = previewErrors();
  const payload = buildPayload();

  return (
    <ModalShell title="Import compliance items" onClose={onClose}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted">Upload a CSV file with a header row (in Excel: File → Save As → “CSV UTF-8”). Required: clientName, serviceType. Optional: jurisdiction, deadline (YYYY-MM-DD), dueDateOffsetDays, status, dataStatus, isRecurring, recurrenceFrequency, periodType, periodValue, assignee1Name/Email, assignee2Name/Email, managerName/Email, estimatedHours, estimatedFeeAmount, estimatedFeeCurrency, remarks.</p>
          <button onClick={() => downloadTemplate('compliance-items-import-template.csv', template)} className="text-xs btn-ghost-accent border px-2 py-1 rounded">Download template</button>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0] ?? null); }}
          className={`border-2 border-dashed rounded-lg px-4 py-6 text-center ${dragOver ? 'border-accent bg-emerald-900/20' : 'border-border'}`}
        >
          <input
            type="file"
            accept=".csv"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            className="hidden"
            id="compliance-import-file"
          />
          <label htmlFor="compliance-import-file" className="cursor-pointer text-sm text-muted hover:text-white">
            {file ? file.name : 'Drop a CSV file here or click to browse'}
          </label>
        </div>

        {rows.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-muted">{rows.length} rows parsed · {payload.length} valid · {errs.length} preview errors</p>
            {errs.length > 0 && (
              <div className="tint-danger rounded-lg p-2 max-h-24 overflow-y-auto">
                {errs.map((e, i) => <p key={i} className="text-xs text-rose-300">Row {e.row}: {e.message}</p>)}
              </div>
            )}
            <div className="bg-bg-base border border-border rounded-lg overflow-hidden overflow-x-auto max-h-48">
              <table className="w-full text-xs">
                <thead className="bg-bg-card text-muted">
                  <tr>
                    {COMPLIANCE_IMPORT_HEADERS.map((h) => <th key={h} className="px-2 py-1 text-left font-medium whitespace-nowrap">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 10).map((r, i) => (
                    <tr key={i} className="border-t border-border/50">
                      {COMPLIANCE_IMPORT_HEADERS.map((h) => <td key={h} className="px-2 py-1 text-white whitespace-nowrap">{r[h] || '—'}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 10 && <p className="text-xs text-muted px-2 py-1">…and {rows.length - 10} more</p>}
            </div>
          </div>
        )}

        {result && (
          <div className={`text-xs p-2 rounded-lg ${result.failed > 0 ? 'tint-danger' : 'tint-success'}`}>
            <p>Created {result.created}, failed {result.failed}</p>
            {result.errors.length > 0 && (
              <div className="max-h-24 overflow-y-auto mt-1 space-y-1">
                {result.errors.map((e, i) => <p key={i}>{e}</p>)}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button onClick={() => run(true)} disabled={running || payload.length === 0} className="text-sm btn-ghost-accent border px-3 py-1.5 rounded-lg disabled:opacity-50">{running ? 'Checking…' : 'Preview'}</button>
          <button onClick={() => run(false)} disabled={running || payload.length === 0} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">{running ? 'Importing…' : 'Import'}</button>
        </div>
      </div>
    </ModalShell>
  );
}


function BulkItemAssignModal({ count, busy, onClose, onSubmit }: {
  count: number;
  busy: boolean;
  onClose: () => void;
  onSubmit: (params: Record<string, unknown>) => void;
}) {
  const [assignee1Id, setAssignee1Id] = useState<string | null | undefined>(undefined);
  const [assignee2Id, setAssignee2Id] = useState<string | null | undefined>(undefined);
  const [managerId, setManagerId] = useState<string | null | undefined>(undefined);
  const canSubmit = assignee1Id !== undefined || assignee2Id !== undefined || managerId !== undefined;
  return (
    <ModalShell title={`Assign ${count} compliance item(s)`} onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-muted mb-1">Assignee 1</label>
          <StaffSelect value={assignee1Id} onChange={setAssignee1Id} />
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Assignee 2</label>
          <StaffSelect value={assignee2Id} onChange={setAssignee2Id} />
          <p className="text-xs text-muted mt-1">Setting assignee 2 on an item without assignee 1 will fail for that item.</p>
        </div>
        <div>
          <label className="block text-xs text-muted mb-1">Manager</label>
          <StaffSelect value={managerId} onChange={setManagerId} />
        </div>
        <p className="text-xs text-muted">Fields left on “keep current” stay unchanged on every selected item.</p>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button
            onClick={() => {
              const params: Record<string, unknown> = {};
              if (assignee1Id !== undefined) params.assignee1Id = assignee1Id;
              if (assignee2Id !== undefined) params.assignee2Id = assignee2Id;
              if (managerId !== undefined) params.managerId = managerId;
              onSubmit(params);
            }}
            disabled={busy || !canSubmit}
            className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {busy ? 'Applying…' : 'Assign'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function BulkItemStatusModal({ count, busy, onClose, onSubmit }: {
  count: number;
  busy: boolean;
  onClose: () => void;
  onSubmit: (status: string) => void;
}) {
  const [nextStatus, setNextStatus] = useState('');
  return (
    <ModalShell title={`Set status for ${count} compliance item(s)`} onClose={onClose}>
      <div className="space-y-3">
        <label className="block text-xs text-muted">New status</label>
        <select
          value={nextStatus}
          onChange={(e) => setNextStatus(e.target.value)}
          className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent"
        >
          <option value="">— select —</option>
          {itemStatusSchema.options.map((s) => (
            <option key={s} value={s}>{ITEM_STATUS_LABELS[s] ?? s}</option>
          ))}
        </select>
        <p className="text-xs text-muted">Completed items are locked for users without the edit-completed capability — those rows come back as failures.</p>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button>
          <button
            onClick={() => onSubmit(nextStatus)}
            disabled={busy || !nextStatus}
            className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
          >
            {busy ? 'Applying…' : 'Set status'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
